// 🎓 Lecture Page - 도메인 훅 기반 FSD 아키텍처
import { lacksCourseAccess } from '../template/courseOwnershipState';
import React, { useMemo, useState } from 'react';
import {
  course_lecture_lecturePage_courseLoading,
  course_lecture_lecturePage_courseError,
  course_lecture_lecturePage_accessCheck,
  course_lecture_lecturePage_lectureLoading,
  course_lecture_lecturePage_noLecture,
  course_lecture_lecturePage_lectureError,
  course_review_modalTitle,
  course_review_submit,
  actions_cancel
} from '@paraglide/messages';

// LectureTemplate import
import LectureTemplate from '../template/LectureTemplate';

// Domain-specific types
import type { SectionDetail } from '@edusense/course-client';

// Local imports
import { LectureTemplateProps } from './LecturePage.types';
import { useLecturePageData, useLectureNavigation, useLectureActions } from './LecturePage.hooks';
import { useMarkdownContent } from './LecturePage.markdown';

// UI Components
import { CourseReviewModal } from '@edusense/features-course';

interface LecturePageProps {
  courseId?: string;
  lectureId?: string;
}

const LecturePage: React.FC<LecturePageProps> = (props) => {
  const [isTocVisible, setIsTocVisible] = useState<boolean>(false);

  // 모든 데이터 로딩 로직 - props를 전달
  const {
    courseId,
    lectureId,
    courseData,
    lectureDetail,
    isCourseLoading,
    isLectureLoading,
    courseError,
    lectureError,
    markLectureCompletedMutation,
    queryClient,
    navigate
  } = useLecturePageData(props.courseId, props.lectureId);

  // 네비게이션 관련 로직
  const {
    curriculum,
    flatLectures,
    currentLectureIndex,
    hasPreviousLecture,
    hasNextLecture
  } = useLectureNavigation(courseData, lectureId);

  // 액션 핸들러들
  const {
    showReviewModal,
    setShowReviewModal,
    handleGoBack,
    handleNavigateLecture,
    handleCompleteCourse,
    handleReviewSubmitSuccess,
    handleQuizSubmit
  } = useLectureActions(
    courseId,
    lectureId,
    lectureDetail?.id,
    markLectureCompletedMutation,
    queryClient,
    navigate,
    flatLectures,
    currentLectureIndex
  );

  // 마크다운 콘텐츠 로딩
  const {
    rawMarkdownContent,
    isMarkdownLoading,
    markdownError
  } = useMarkdownContent(courseData, lectureDetail as any);

  const normalizedCurriculum = useMemo(() => {
    if (!Array.isArray(curriculum)) {
      return [] as SectionDetail[];
    }

    return curriculum.map(section => ({
      ...section,
      id: section?.id?.toString() ?? '',
      lectures: section?.lectures?.map(lecture => ({
        ...lecture,
        id: lecture?.id?.toString() ?? '',
      })) ?? [],
    })) as SectionDetail[];
  }, [curriculum]);

  const handleToggleToc = () => {
    setIsTocVisible(prev => !prev);
  };

  // --- Loading/Error checks and Returns ---
  const combinedLoading = isCourseLoading || isLectureLoading;

  // Update loading/error checks to account for ownership check phase
  if (isCourseLoading || (!courseData && !courseError)) {
    return <div>{course_lecture_lecturePage_courseLoading()}</div>;
  }

  if (courseError && !courseData) {
    return <div>{course_lecture_lecturePage_courseError()}</div>;
  }

  // If course data is loaded, but ownership is false (redirect should happen soon)
  if (lacksCourseAccess(courseData?.summary)) {
    return <div>{course_lecture_lecturePage_accessCheck()}</div>;
  }

  // At this point, user owns the course. Check lecture loading status.
  if (combinedLoading && !lectureDetail) {
    return <div>{course_lecture_lecturePage_lectureLoading()}</div>;
  }

  if (lectureError && !lectureDetail) {
    return <div>{course_lecture_lecturePage_lectureError()}</div>;
  }

  if (!lectureDetail) {
    return <div>{course_lecture_lecturePage_noLecture()}</div>;
  }

  // --- Prepare props ---
  // Debug logging to understand data structure
  console.log('📚 Lecture Detail:', {
    id: lectureDetail.id,
    title: lectureDetail.title,
    contentType: lectureDetail.contentType,
    videoUrl: lectureDetail.videoUrl,
    content: lectureDetail.content?.substring(0, 100),
    markdownContent: rawMarkdownContent?.substring(0, 100),
    rawLectureDetail: lectureDetail
  });

  const lecturePropsForTemplate: LectureTemplateProps = {
    id: lectureDetail.id.toString(),
    title: lectureDetail.title,
    videoUrl: lectureDetail.videoUrl ?? undefined,
    description: undefined, // API doesn't provide description field
    markdownContent: rawMarkdownContent ?? undefined,
    contentType: lectureDetail.contentType,
    // quizData logic might need adjustment based on how quizzes are handled now
    // quizData: lectureDetail.quizData ?? null,
  };

  // --- Render ---
  // 마지막 강의 여부 계산
  const isLastLecture = !hasNextLecture;

  return (
    <>
      <LectureTemplate
        courseId={courseId!} // Assuming courseId is present
        currentLectureId={lectureId!} // Assuming lectureId is present
        lecture={lecturePropsForTemplate}
        curriculum={normalizedCurriculum}
        isTocVisible={isTocVisible}
        onToggleToc={handleToggleToc}
        onGoBack={handleGoBack}
        onNavigatePrevious={hasPreviousLecture ? () => handleNavigateLecture('previous') : undefined}
        onNavigateNext={hasNextLecture ? () => handleNavigateLecture('next') : undefined}
        onCompleteCourse={isLastLecture ? handleCompleteCourse : undefined}
        isPreviousEnabled={hasPreviousLecture}
        isNextEnabled={hasNextLecture}
        isLastLecture={isLastLecture}
        isMarkdownLoading={isMarkdownLoading}
        markdownError={markdownError?.message}
        onQuizSubmit={handleQuizSubmit}
      />

      {/* 리뷰 모달 컴포넌트 사용 */}
      {showReviewModal && (
        <CourseReviewModal
          courseId={courseId!}
          onClose={() => setShowReviewModal(false)}
          onSubmitSuccess={handleReviewSubmitSuccess}
        />
      )}
    </>
  );
};

export default LecturePage;
